import { useMutation } from "react-query";

import { MAX_FILE_SIZE, MAX_FILE_SIZE_ERROR } from "../../helpers/files";
import { DeepPartial } from "../../helpers/typescript";
import { useApi } from "../../providers/ApiProvider";
import { APQueryOptions, useAPQuery } from "../../providers/APQueryProvider";
import { AssetAndLiability, FinanceApplication } from "../../types/Dossier";

export type FinanceApplicationFileUploads = {
  driversLicenseFrontImage?: File;
  driversLicenseBackImage?: File;
  passportImage?: File;
  visaImage?: File;
  lastTaxReturnImage?: File;
  prevTaxReturnImage?: File;
  latestPayslipImage?: File;
  prevPayslipImage?: File;
  spouseLastTaxReturnImage?: File;
  spousePrevTaxReturnImage?: File;
  spouseLatestPayslipImage?: File;
  spouseIncomeDeclarationImage?: File;
};

export type AssetsAndLiabilitiesUpdatePayload = {
  add?: Omit<AssetAndLiability, "id" | "financeId" | "name" | "typeLabel">[];
  remove?: string[];
  update?: (Pick<AssetAndLiability, "id"> &
    Partial<
      Omit<AssetAndLiability, "id" | "financeId" | "name" | "typeLabel">
    >)[];
  totalAssets?: number | null;
  totalLiabilities?: number | null;
  totalNetWorth?: number | null;
};

export type UpdateFinanceApplicationPayload = DeepPartial<
  Omit<FinanceApplication, "assetsAndLiabilities">
> & {
  fileUploads?: FinanceApplicationFileUploads;
  assetsAndLiabilities?: AssetsAndLiabilitiesUpdatePayload;
};

function buildImagesPayload(payload: FinanceApplicationFileUploads): FormData {
  const formData = new FormData();

  for (const key in payload) {
    const value = payload[key as keyof FinanceApplicationFileUploads];

    if (value) {
      if (value.size > MAX_FILE_SIZE) {
        throw new Error(`${key} ${MAX_FILE_SIZE_ERROR}`);
      } else {
        formData.append(key, value);
      }
    }
  }

  return formData;
}

export const updateFinanceApplication = (
  dossierId: string,
  options: APQueryOptions = {}
) => {
  const { fetchWithAuth } = useApi();
  const { defaultOnMutateError, defaultOnMutateSuccess } = useAPQuery();

  return useMutation({
    mutationFn: async (
      payload: UpdateFinanceApplicationPayload
    ): Promise<FinanceApplication | null> => {
      const { fileUploads, ...financeApplication } = payload;

      if (fileUploads) {
        const imagesFormData = buildImagesPayload(fileUploads);

        await fetchWithAuth<null>("updateFinanceApplicationImages", dossierId, {
          requestOptions: {
            method: "PATCH",
            body: imagesFormData,
            excludeContentTypeHeader: true,
          },
        });
      }

      if (Object.keys(financeApplication).length > 0) {
        return fetchWithAuth<FinanceApplication>(
          "updateFinanceApplication",
          dossierId,
          {
            requestOptions: {
              method: "PATCH",
              body: JSON.stringify(financeApplication),
            },
          }
        );
      } else {
        return null;
      }
    },
    onError: defaultOnMutateError(options),
    onSuccess: defaultOnMutateSuccess(options),
  });
};
